import React from 'react';
import styled from 'styled-components';

import { Items, ProductParamInput } from 'containers/ProductRegist/hooks/types';
import Row from 'components/common/Row';
import Column from 'components/common/Column';
import Text from 'components/common/Text';
import Divider from 'components/Divider';
import { ONLY_NUMBER_REGEX } from 'utils/regExp';
import { InventoryTargetType } from 'types/api';
import useNumberInputProps from 'hooks/useNumberInputProps';

const OptionSettingBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 16px;
  width: 100%;

  border-radius: 8px;
  border: 1px solid #dbdcdf;
  background: #fff;
`;

const OptionInput = styled.input`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 8px 27px 8px 12px;
  width: 100%;

  font-size: 14px;
  text-align: right;
  border-radius: 8px;
  border: 1px solid #dbdcdf;
  background: #fff;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &::placeholder {
    justify-content: flex-end;
    color: #c2c4c8;
  }

  &:focus {
    border: 1.5px solid #1b1c1e;
  }
`;

interface PriceInputProps {
  value: number;
  onChangeValue: (value: number) => void;
}

// renderSaleForm 은 분기마다 호출돼 훅을 쓸 수 없어 입력칸만 컴포넌트로 뺀다
const PriceInput = ({ value, onChangeValue }: PriceInputProps) => {
  const inputProps = useNumberInputProps(value, onChangeValue);

  return <OptionInput {...inputProps} />;
};

interface Props {
  isMobile: boolean;
  productParamInput: ProductParamInput;
  item: Items;
  type: string;
  handleChangeItems: (key: string, value: any) => void;
}

const OptionContents = (props: Props) => {
  const { isMobile, productParamInput, item, type, handleChangeItems } = props;

  const handleSetOptionCount = (e: any) => {
    if (productParamInput.firstOpenedAt === null) {
      handleChangeItems(
        'quota',
        Number(e.target.value.replaceAll(ONLY_NUMBER_REGEX, '')),
      );

      return;
    }

    if (productParamInput.firstOpenedAt !== null && !item.status) {
      handleChangeItems(
        'quota',
        Number(e.target.value.replaceAll(ONLY_NUMBER_REGEX, '')),
      );

      return;
    }

    if (type && item.quota !== 0) {
      if (Number(e.target.value) === 0) {
        handleChangeItems('quota', 1);

        return;
      }

      handleChangeItems(
        'quota',
        Number(e.target.value.replaceAll(ONLY_NUMBER_REGEX, '')),
      );

      return;
    }

    handleChangeItems(
      'quota',
      Number(e.target.value.replaceAll(ONLY_NUMBER_REGEX, '')),
    );
  };

  const handleChangeSalePrice = (type: string, value: number) => {
    handleChangeItems('price', {
      ...item.price,
      [type]: value,
    });
  };

  // 판매가 정가 인풋 컨테이너
  const renderSaleForm = (type: string): JSX.Element => {
    if (isMobile) {
      return (
        <Row justifyContent="space-between" style={{ width: '100%' }}>
          <Text color="#1b1c1e" size="14px" weight={400}>
            {type === 'retail' ? '정가' : '판매가'}
          </Text>
          <Row style={{ position: 'relative', width: '50%' }}>
            <PriceInput
              value={type === 'retail' ? item.price.retail : item.price.sale}
              onChangeValue={value => handleChangeSalePrice(type, value)}
            />
            <Text
              color="#1b1c1e"
              size="14px"
              weight={400}
              style={{ position: 'absolute', right: '12px' }}
            >
              원
            </Text>
          </Row>
        </Row>
      );
    }
    return (
      <Column gap="8px" align="flex-start">
        <Text color="#1b1c1e" size="14px" weight={400}>
          {type === 'retail' ? '정가' : '판매가'}
        </Text>
        <Row style={{ position: 'relative', width: '100%' }}>
          <PriceInput
            value={type === 'retail' ? item.price.retail : item.price.sale}
            onChangeValue={value => handleChangeSalePrice(type, value)}
          />
          <Text
            color="#1b1c1e"
            size="14px"
            weight={400}
            style={{ position: 'absolute', right: '12px' }}
          >
            원
          </Text>
        </Row>
      </Column>
    );
  };

  // 지정형 일정별 인원제한
  const renderOptionSchedule = (): JSX.Element => {
    if (isMobile) {
      return (
        <OptionSettingBox>
          <Column gap="8px">
            {renderSaleForm('retail')}
            {renderSaleForm('sale')}
          </Column>
        </OptionSettingBox>
      );
    }

    return (
      <OptionSettingBox>
        <Row gap="8px" style={{ width: '100%' }}>
          {renderSaleForm('retail')}
          {renderSaleForm('sale')}
        </Row>
      </OptionSettingBox>
    );
  };

  // 모집 인원
  const renderScheduleQuota = (): JSX.Element => {
    return (
      <Column gap="12px" align="flex-start">
        <Text color="#1b1c1e" size="14px" weight={400}>
          모집 인원
        </Text>
        <Row gap="8px" style={{ width: '100%' }}>
          <Row style={{ position: 'relative', width: '50%' }}>
            <Text
              color="#1b1c1e"
              size="14px"
              weight={400}
              style={{ position: 'absolute', left: '12px' }}
            >
              최소
            </Text>
            <OptionInput
              placeholder="제한없음"
              type="text"
              value={item.minimumQuota === 0 ? '제한 없음' : item.minimumQuota}
              onChange={(e: any) => {
                handleChangeItems(
                  'minimumQuota',
                  Number(e.target.value.replaceAll(ONLY_NUMBER_REGEX, '')),
                );
              }}
              style={{
                padding:
                  item.minimumQuota === 0 ? '8px 12px' : '8px 27px 8px 12px',
              }}
            />
            {item.minimumQuota !== 0 && (
              <Text
                color="#1b1c1e"
                size="14px"
                weight={400}
                style={{ position: 'absolute', right: '12px' }}
              >
                명
              </Text>
            )}
          </Row>
          <Text color="#46464c" size="16px" weight={700}>
            ~
          </Text>
          <Row style={{ position: 'relative', width: '50%' }}>
            <Text
              color="#1b1c1e"
              size="14px"
              weight={400}
              style={{ position: 'absolute', left: '12px' }}
            >
              최대
            </Text>
            <OptionInput
              placeholder="0"
              type="text"
              value={item.quota}
              onChange={(e: any) => {
                handleChangeItems(
                  'quota',
                  Number(e.target.value.replaceAll(ONLY_NUMBER_REGEX, '')),
                );
              }}
            />
            <Text
              color="#1b1c1e"
              size="14px"
              weight={400}
              style={{ position: 'absolute', right: '12px' }}
            >
              명
            </Text>
          </Row>
        </Row>
      </Column>
    );
  };

  // 지정형 옵션별 인원제한
  const renderOptionItems = (): JSX.Element => {
    if (isMobile) {
      return (
        <OptionSettingBox>
          <Column gap="16px">
            <Column gap="8px">
              {renderSaleForm('retail')}
              {renderSaleForm('sale')}
            </Column>
            <Divider />
            {renderScheduleQuota()}
          </Column>
        </OptionSettingBox>
      );
    }

    return (
      <OptionSettingBox>
        <Row gap="24px">
          <Row gap="12px" style={{ width: '100%' }}>
            {renderSaleForm('retail')}
            {renderSaleForm('sale')}
          </Row>
          <Divider
            style={{
              width: '0px',
              height: '56px',
              border: '1px solid #eaebec',
            }}
          />
          {renderScheduleQuota()}
        </Row>
      </OptionSettingBox>
    );
  };

  // 판매 수량
  const renderOptionSaleCount = (): JSX.Element => {
    if (isMobile) {
      return (
        <Row justifyContent="space-between" style={{ width: '100%' }}>
          <Text color="#1b1c1e" size="14px" weight={400}>
            판매 수량
          </Text>
          <Row style={{ position: 'relative', width: '50%' }}>
            <OptionInput
              placeholder="제한없음"
              type="text"
              disabled={
                type &&
                item.status &&
                productParamInput.firstOpenedAt !== null &&
                item.quota === 0
              }
              value={item.quota === 0 ? '' : item.quota}
              onChange={(e: any) => {
                handleSetOptionCount(e);
              }}
              style={{
                padding: item.quota === 0 ? '8px 12px' : '8px 27px 8px 12px',
              }}
            />
            {item.quota !== 0 && (
              <Text
                color="#1b1c1e"
                size="14px"
                weight={400}
                style={{ position: 'absolute', right: '12px' }}
              >
                개
              </Text>
            )}
          </Row>
        </Row>
      );
    }

    return (
      <Column align="flex-start" gap="8px" style={{ width: '50%' }}>
        <Text color="#1b1c1e" size="14px" weight={400}>
          판매 수량
        </Text>
        <Row style={{ position: 'relative', width: '100%' }}>
          <OptionInput
            placeholder="제한없음"
            type="text"
            disabled={
              type &&
              item.status &&
              productParamInput.firstOpenedAt !== null &&
              item.quota === 0
            }
            value={item.quota === 0 ? '' : item.quota}
            onChange={(e: any) => {
              handleSetOptionCount(e);
            }}
            style={{
              padding: item.quota === 0 ? '8px 12px' : '8px 27px 8px 12px',
            }}
          />
          {item.quota !== 0 && (
            <Text
              color="#1b1c1e"
              size="14px"
              weight={400}
              style={{ position: 'absolute', right: '12px' }}
            >
              개
            </Text>
          )}
        </Row>
      </Column>
    );
  };

  // 조율형 옵션
  const renderItemOption = (): JSX.Element => {
    if (isMobile) {
      return (
        <OptionSettingBox>
          <Column gap="16px">
            {renderOptionSaleCount()}
            <Divider />
            <Column gap="8px" style={{ width: '100%' }}>
              {renderSaleForm('retail')}
              {renderSaleForm('sale')}
            </Column>
          </Column>
        </OptionSettingBox>
      );
    }

    return (
      <OptionSettingBox>
        <Row gap="24px" style={{ width: '100%' }}>
          {renderOptionSaleCount()}
          <Divider
            style={{
              width: '0px',
              height: '56px',
              border: '1px solid #eaebec',
            }}
          />
          <Row gap="12px" style={{ width: '100%' }}>
            {renderSaleForm('retail')}
            {renderSaleForm('sale')}
          </Row>
        </Row>
      </OptionSettingBox>
    );
  };

  const renderOptionContents = () => {
    if (productParamInput.frip!.attachedToSchedule) {
      if (
        productParamInput.inventoryTargetType ===
        InventoryTargetType.BY_SCHEDULE
      ) {
        return renderOptionSchedule(); // 지정형 일정별 인원 제한
      }

      return renderOptionItems(); // 지정형 옵션별 인원 제한
    }

    return renderItemOption();
  };

  return renderOptionContents();
};

export default OptionContents;
